import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  Plus, Search, Phone, Mail, MapPin, Building, MoreVertical,
  Edit, Trash2, Eye, Loader2, AlertTriangle, Users, User,
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api/client'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { Modal } from '../../components/ui/Modal'
import { Dropdown } from '../../components/ui/Dropdown'
import { Skeleton } from '../../components/ui/Skeleton'
import { Pagination } from '../../components/ui/Pagination'
import { usePermissions } from '../../stores/authStore'
import toast from 'react-hot-toast'

interface Customer {
  id: string
  name: string
  phone?: string
  email?: string
  address?: string
  city?: string
  company_name?: string
  national_id?: string
  created_at: string
}

interface CustomerListResponse {
  items: Customer[]
  total: number
  total_pages: number
  page: number
  page_size: number
}

export default function CustomersPage() {
  const { hasPermission } = usePermissions()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [showForm, setShowForm] = useState(false)
  const [editCustomer, setEditCustomer] = useState<Customer | null>(null)
  const [deleteId, setDeleteId] = useState<string | null>(null)

  // Form state
  const [form, setForm] = useState({
    name: '', phone: '', email: '', address: '', city: '',
    company_name: '', national_id: '', postal_code: '', tax_number: '',
  })

  const { data, isLoading, error } = useQuery({
    queryKey: ['customers', page, search],
    queryFn: async (): Promise<CustomerListResponse> => {
      const res = await api.get('/customers', { params: { page, page_size: 20, search } })
      return res.data
    },
  })

  const createMutation = useMutation({
    mutationFn: (data: typeof form) => api.post('/customers', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      toast.success('مشتری ایجاد شد')
      setShowForm(false)
      resetForm()
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'خطا در ایجاد مشتری'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/customers/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      toast.success('مشتری حذف شد')
      setDeleteId(null)
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'خطا در حذف'),
  })

  const resetForm = () => setForm({ name: '', phone: '', email: '', address: '', city: '', company_name: '', national_id: '', postal_code: '', tax_number: '' })

  const handleSubmit = () => {
    if (!form.name.trim()) return toast.error('نام مشتری الزامی است')
    createMutation.mutate(form)
  }

  const totalPages = data?.total_pages || 1

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-white">مشتریان</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">{data?.total || 0} مشتری ثبت شده</p>
        </div>
        <Button onClick={() => { resetForm(); setEditCustomer(null); setShowForm(true) }} disabled={!hasPermission('customer', 'create')}>
          <Plus className="w-4 h-4 ml-1" /> مشتری جدید
        </Button>
      </motion.div>

      {/* Search */}
      <div className="card p-4">
        <div className="relative max-w-md">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input placeholder="جستجو در نام، تلفن، ایمیل..." value={search} onChange={e => { setSearch(e.target.value); setPage(1) }} className="pl-10" />
        </div>
      </div>

      {/* List */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-6 space-y-4">
            {Array.from({ length: 5 }).map((_, i) => <div key={i} className="flex items-center gap-4 p-4"><Skeleton className="w-12 h-12 rounded-xl" /><div className="flex-1"><Skeleton className="w-32 h-4 mb-2" /><Skeleton className="w-48 h-3" /></div></div>)}
          </div>
        ) : error ? (
          <div className="p-12 text-center">
            <AlertTriangle className="w-12 h-12 text-danger-500 mx-auto mb-4" />
            <p className="text-danger-600">خطا در بارگذاری مشتریان</p>
          </div>
        ) : data?.items.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
            <h3 className="font-semibold text-neutral-900 dark:text-white">هیچ مشتری یافت نشد</h3>
            <p className="text-sm text-neutral-500 mt-1">اولین مشتری را اضافه کنید</p>
            <Button onClick={() => setShowForm(true)} className="mt-4"><Plus className="w-4 h-4 ml-1" /> افزودن مشتری</Button>
          </div>
        ) : (
          <>
            <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {data!.items.map((c, i) => (
                <motion.div key={c.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
                  className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-neutral-900 dark:text-white truncate">{c.name}</p>
                    <div className="flex items-center gap-2 sm:gap-3 text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 flex-wrap">
                      {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>}
                      {c.email && <span className="hidden sm:flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>}
                      {c.city && <span className="hidden md:flex items-center gap-1"><MapPin className="w-3 h-3" />{c.city}</span>}
                      {c.company_name && <span className="hidden lg:flex items-center gap-1"><Building className="w-3 h-3" />{c.company_name}</span>}
                    </div>
                  </div>
                  <Dropdown trigger={<button className="p-2 rounded-lg text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800"><MoreVertical className="w-4 h-4" /></button>}
                    items={[
                      { label: 'مشاهده', icon: Eye, onClick: () => {} },
                      { label: 'ویرایش', icon: Edit, onClick: () => { setEditCustomer(c); setForm({ name: c.name, phone: c.phone || '', email: c.email || '', address: c.address || '', city: c.city || '', company_name: c.company_name || '', national_id: c.national_id || '', postal_code: '', tax_number: '' }); setShowForm(true) }, disabled: !hasPermission('customer', 'update') },
                      { type: 'divider' },
                      { label: 'حذف', icon: Trash2, className: 'text-danger-600', onClick: () => setDeleteId(c.id), disabled: !hasPermission('customer', 'delete') },
                    ]} />
                </motion.div>
              ))}
            </div>
            {totalPages > 1 && (
              <div className="p-4 border-t border-neutral-200 dark:border-neutral-800 flex justify-center">
                <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
              </div>
            )}
          </>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal isOpen={showForm} onClose={() => { setShowForm(false); setEditCustomer(null) }} title={editCustomer ? 'ویرایش مشتری' : 'مشتری جدید'} size="md">
        <div className="space-y-4">
          <Input label="نام *" placeholder="نام مشتری" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          <div className="grid grid-cols-2 gap-4">
            <Input label="تلفن" placeholder="۰۹۱۲۱۲۳۴۵۶۷" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
            <Input label="ایمیل" type="email" placeholder="user@example.com" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input label="شهر" placeholder="تهران" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} />
            <Input label="شرکت" placeholder="نام شرکت" value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} />
          </div>
          <Input label="آدرس" placeholder="آدرس کامل" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
          <Input label="کد ملی" placeholder="کد ملی" value={form.national_id} onChange={e => setForm({ ...form, national_id: e.target.value })} />
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => { setShowForm(false); setEditCustomer(null) }}>انصراف</Button>
            <Button onClick={handleSubmit} disabled={createMutation.isPending}>
              {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin ml-1" /> : null}
              {editCustomer ? 'ذخیره' : 'ایجاد'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirm */}
      <Modal isOpen={!!deleteId} onClose={() => setDeleteId(null)} title="حذف مشتری" size="sm">
        <p className="text-neutral-600 dark:text-neutral-400">آیا از حذف این مشتری مطمئن هستید؟</p>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => setDeleteId(null)}>انصراف</Button>
          <Button variant="danger" onClick={() => deleteId && deleteMutation.mutate(deleteId)} disabled={deleteMutation.isPending}>حذف</Button>
        </div>
      </Modal>
    </div>
  )
}